import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import {
  ChevronDown, ChevronRight, Plus, Edit3, Trash2, ArrowUp, ArrowDown,
  GitMerge, Search, ExternalLink, Package,
} from "lucide-react";

/**
 * Admin Category Manager
 * - Tree view with expand/collapse
 * - Create / edit / delete / disable / re-parent / reorder
 * - Search, book count, "preview page", merge, cascade-delete
 */
export default function CategoryManager() {
  const [flat, setFlat] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // null | {mode:'create'|'edit', category, parent}
  const [merging, setMerging] = useState(null); // {source}
  const [mergeTarget, setMergeTarget] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/categories/flat");
      setFlat(data);
      // auto-expand top-level
      if (expanded.size === 0) {
        setExpanded(new Set(data.filter((c) => c.level === 0).map((c) => c.id)));
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  // Build children index
  const childrenByParent = useMemo(() => {
    const map = new Map();
    for (const c of flat) {
      const key = c.parent_id || "__root__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.sort_order - b.sort_order) || a.name.localeCompare(b.name));
    }
    return map;
  }, [flat]);

  const roots = childrenByParent.get("__root__") || [];

  const toggle = (id) => {
    const s = new Set(expanded);
    s.has(id) ? s.delete(id) : s.add(id);
    setExpanded(s);
  };

  const move = async (cat, direction) => {
    const siblings = childrenByParent.get(cat.parent_id || "__root__") || [];
    const idx = siblings.findIndex((c) => c.id === cat.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const other = siblings[swapIdx];
    await api.post("/categories/reorder", {
      items: [
        { id: cat.id, parent_id: cat.parent_id || null, sort_order: other.sort_order },
        { id: other.id, parent_id: other.parent_id || null, sort_order: cat.sort_order },
      ],
    });
    await load();
  };

  const toggleActive = async (cat) => {
    await api.put(`/categories/${cat.id}`, { is_active: !cat.is_active });
    await load();
  };

  const del = async (cat) => {
    const hasChildren = (childrenByParent.get(cat.id) || []).length > 0;
    let cascade = false;
    if (hasChildren) {
      if (!window.confirm(
        `"${cat.name}" has subcategories. Delete this category AND all its subcategories?\n\nThis will also unassign all books from these categories.`
      )) return;
      cascade = true;
    } else {
      if (!window.confirm(`Delete "${cat.name}"? Books will be unassigned from this category.`)) return;
    }
    try {
      await api.delete(`/categories/${cat.id}${cascade ? "?cascade=true" : ""}`);
      toast.success("Category deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const doMerge = async () => {
    if (!merging || !mergeTarget) return;
    if (merging.source.id === mergeTarget) { toast.error("Cannot merge into itself"); return; }
    try {
      await api.post("/categories/merge", { source_id: merging.source.id, target_id: mergeTarget });
      toast.success("Categories merged");
      setMerging(null);
      setMergeTarget("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    }
  };

  // Filter for search
  const visibleIds = useMemo(() => {
    if (!query.trim()) return null;
    const q = query.toLowerCase();
    const matched = new Set();
    for (const c of flat) {
      if (c.name.toLowerCase().includes(q) || (c.slug || "").includes(q)) {
        matched.add(c.id);
        // include ancestors so tree shows path
        let pid = c.parent_id;
        while (pid) {
          matched.add(pid);
          const parent = flat.find((x) => x.id === pid);
          pid = parent?.parent_id;
        }
      }
    }
    return matched;
  }, [flat, query]);

  const renderNode = (cat, depth = 0) => {
    if (visibleIds && !visibleIds.has(cat.id)) return null;
    const children = childrenByParent.get(cat.id) || [];
    const isOpen = expanded.has(cat.id) || !!visibleIds;
    const hasChildren = children.length > 0;
    return (
      <div key={cat.id} data-testid={`cat-node-${cat.id}`}>
        <div
          className="flex items-center gap-2 py-2 px-2 border-b border-[#E5E1D8] hover:bg-[#F0EBE0]/40 group"
          style={{ paddingLeft: `${depth * 20 + 8}px` }}
        >
          <button
            onClick={() => hasChildren && toggle(cat.id)}
            className={`w-5 flex-shrink-0 ${!hasChildren && "invisible"}`}
            data-testid={`cat-toggle-${cat.id}`}
          >
            {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
          <div className="flex-1 flex items-center gap-3 min-w-0">
            <span className={`font-serif truncate ${cat.is_active ? "text-[#1A1714]" : "text-[#1A1714]/40 italic line-through"}`}>
              {cat.name}
            </span>
            <span className="font-mono text-[10px] text-[#1A1714]/40 truncate">/{cat.slug}</span>
            <span className="inline-flex items-center gap-1 font-mono text-[10px] text-[#1A1714]/60 whitespace-nowrap">
              <Package size={11} /> {cat.direct_book_count || 0}
            </span>
          </div>
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => move(cat, "up")} title="Move up" data-testid={`cat-up-${cat.id}`}
              className="p-1 hover:text-[#7A2021]"><ArrowUp size={13} /></button>
            <button onClick={() => move(cat, "down")} title="Move down" data-testid={`cat-down-${cat.id}`}
              className="p-1 hover:text-[#7A2021]"><ArrowDown size={13} /></button>
            <button onClick={() => setEditing({ mode: "create", parent: cat })} title="Add child" data-testid={`cat-add-child-${cat.id}`}
              className="p-1 hover:text-[#7A2021]"><Plus size={13} /></button>
            <button onClick={() => setEditing({ mode: "edit", category: cat })} title="Edit" data-testid={`cat-edit-${cat.id}`}
              className="p-1 hover:text-[#7A2021]"><Edit3 size={13} /></button>
            <button onClick={() => setMerging({ source: cat })} title="Merge into…" data-testid={`cat-merge-${cat.id}`}
              className="p-1 hover:text-[#7A2021]"><GitMerge size={13} /></button>
            <div title={cat.is_active ? "Disable" : "Enable"} className="p-1">
              <Switch
                checked={cat.is_active}
                onCheckedChange={() => toggleActive(cat)}
                data-testid={`cat-toggle-active-${cat.id}`}
                className="scale-75"
              />
            </div>
            <a href={`/books/${(cat.path || [cat.slug]).join("/")}`} target="_blank" rel="noreferrer" title="Preview" data-testid={`cat-preview-${cat.id}`}
              className="p-1 hover:text-[#7A2021]"><ExternalLink size={13} /></a>
            <button onClick={() => del(cat)} title="Delete" data-testid={`cat-delete-${cat.id}`}
              className="p-1 hover:text-red-600"><Trash2 size={13} /></button>
          </div>
        </div>
        {isOpen && hasChildren && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className="mt-8" data-testid="admin-categories">
      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={() => setEditing({ mode: "create", parent: null })}
          data-testid="cat-new-root"
          className="rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial">
          <Plus size={14} className="mr-2" /> New top-level
        </Button>
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#1A1714]/40" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search categories…"
            className="pl-8 rounded-none border-[#E5E1D8]"
            data-testid="cat-search" />
        </div>
        <span className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60" data-testid="cat-count">
          {flat.length} categories
        </span>
      </div>

      <div className="mt-6 border border-[#E5E1D8] bg-[#F9F6F0]" data-testid="cat-tree">
        {loading && <div className="p-6 text-center font-mono text-xs text-[#1A1714]/50">Loading…</div>}
        {!loading && roots.length === 0 && (
          <div className="p-6 text-center font-mono text-xs text-[#1A1714]/50">No categories yet.</div>
        )}
        {!loading && roots.map((r) => renderNode(r, 0))}
      </div>

      <CategoryFormDialog
        open={!!editing}
        state={editing}
        allCategories={flat}
        onClose={() => setEditing(null)}
        onSaved={load}
      />

      <Dialog open={!!merging} onOpenChange={(o) => !o && setMerging(null)}>
        <DialogContent className="bg-[#F9F6F0] rounded-none border-[#E5E1D8]" data-testid="cat-merge-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Merge "{merging?.source?.name}" into…</DialogTitle>
            <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
              All books & children will move to the target. Source will be deleted.
            </DialogDescription>
          </DialogHeader>
          <select value={mergeTarget} onChange={(e) => setMergeTarget(e.target.value)}
            data-testid="cat-merge-target"
            className="w-full h-10 rounded-none border border-[#E5E1D8] bg-white px-3 font-mono text-xs">
            <option value="">— Select target —</option>
            {flat.filter((c) => c.id !== merging?.source?.id).map((c) => (
              <option key={c.id} value={c.id}>{"— ".repeat(c.level)}{c.name}</option>
            ))}
          </select>
          <Button onClick={doMerge} disabled={!mergeTarget}
            data-testid="cat-merge-confirm"
            className="w-full rounded-none bg-[#7A2021] text-[#F9F6F0] hover:bg-[#1A1714]">
            Merge
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryFormDialog({ open, state, allCategories, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: "", slug: "", description: "", parent_id: null,
    image: "", icon: "", is_active: true,
    seo_title: "", meta_description: "", keywords: "",
  });

  useEffect(() => {
    if (!state) return;
    if (state.mode === "edit") {
      const c = state.category;
      setForm({
        name: c.name || "",
        slug: c.slug || "",
        description: c.description || "",
        parent_id: c.parent_id || null,
        image: c.image || "",
        icon: c.icon || "",
        is_active: c.is_active !== false,
        seo_title: c.seo_title || "",
        meta_description: c.meta_description || "",
        keywords: (c.keywords || []).join(", "),
      });
    } else {
      setForm({
        name: "", slug: "", description: "",
        parent_id: state.parent?.id || null,
        image: "", icon: "", is_active: true,
        seo_title: "", meta_description: "", keywords: "",
      });
    }
  }, [state]);

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim() || undefined,
      description: form.description || undefined,
      parent_id: form.parent_id || null,
      image: form.image || undefined,
      icon: form.icon || undefined,
      is_active: form.is_active,
      seo_title: form.seo_title || undefined,
      meta_description: form.meta_description || undefined,
      keywords: form.keywords.split(",").map((k) => k.trim()).filter(Boolean),
    };
    try {
      if (state.mode === "edit") {
        await api.put(`/categories/${state.category.id}`, payload);
        toast.success("Category updated");
      } else {
        await api.post("/categories", payload);
        toast.success("Category created");
      }
      onClose();
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#F9F6F0] rounded-none border-[#E5E1D8] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cat-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {state?.mode === "edit" ? "Edit category" : (state?.parent ? `New child of "${state.parent.name}"` : "New top-level category")}
          </DialogTitle>
          <DialogDescription className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60">
            {state?.mode === "edit" ? "Update category name, hierarchy and SEO settings." : "Add a new category. Slug is auto-generated from the name if left blank."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="cat-form">
          <Input required placeholder="Name" value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="rounded-none" data-testid="cat-form-name" />
          <Input placeholder="Slug (auto if blank)" value={form.slug}
            onChange={(e) => setForm({ ...form, slug: e.target.value })}
            className="rounded-none font-mono text-xs" data-testid="cat-form-slug" />
          <select value={form.parent_id || ""}
            onChange={(e) => setForm({ ...form, parent_id: e.target.value || null })}
            data-testid="cat-form-parent"
            className="w-full h-10 rounded-none border border-[#E5E1D8] bg-white px-3 font-mono text-xs">
            <option value="">— Top-level (no parent) —</option>
            {allCategories
              .filter((c) => state?.mode !== "edit" || c.id !== state.category.id)
              .map((c) => <option key={c.id} value={c.id}>{"— ".repeat(c.level)}{c.name}</option>)}
          </select>
          <Textarea placeholder="Description (optional)" value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="rounded-none" data-testid="cat-form-desc" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="Image URL" value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
              className="rounded-none" data-testid="cat-form-image" />
            <Input placeholder="Icon (emoji or URL)" value={form.icon}
              onChange={(e) => setForm({ ...form, icon: e.target.value })}
              className="rounded-none" data-testid="cat-form-icon" />
          </div>
          <div className="pt-2 border-t border-[#E5E1D8]">
            <p className="font-mono text-[10px] uppercase tracking-editorial text-[#1A1714]/60 mb-2">SEO</p>
            <Input placeholder="SEO title" value={form.seo_title}
              onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
              className="rounded-none mb-2" data-testid="cat-form-seo-title" />
            <Textarea placeholder="Meta description" value={form.meta_description}
              onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
              className="rounded-none mb-2" data-testid="cat-form-meta-desc" />
            <Input placeholder="Keywords (comma-separated)" value={form.keywords}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              className="rounded-none" data-testid="cat-form-keywords" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })}
              data-testid="cat-form-active" />
            <span className="font-mono text-xs">{form.is_active ? "Active" : "Disabled"}</span>
          </div>
          <Button type="submit"
            className="w-full rounded-none bg-[#1A1714] text-[#F9F6F0] hover:bg-[#7A2021] font-mono text-xs uppercase tracking-editorial"
            data-testid="cat-form-submit">
            Save
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
